import classNames from "classnames";
import Button from "components/button";
import ButtonLoader from "components/button-loader";
import CheckPermission from "components/check-permission/CheckPermission";
import Input from "components/input";
import type global from "global";
import React, { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";
import { CSSTransition } from "react-transition-group";
import request from "request";
import { useDispatch, useSelector } from "store/hooks";
import CreateModal from "./components/create-modal";
import modalStyles from "./components/create-modal.module.scss";
import EditUserModal from "./components/editPasswordUser";
import EditUserPermissionsModal from "./components/editPermissionsUser";
import UpdateEmailModal from "./components/updateEmailModal";
import UpdateRoleModal from "./components/updateRoleModal";
import type users from "./users";
import styles from "./users.module.scss";

let timeout: NodeJS.Timeout;

const Users = () => {
	const resultRef = useRef<HTMLDivElement | null>(null);
	const user = useSelector((state) => state.user);

	const [search, setSearch] = useState("");
	const [pending, setPending] = useState(true);
	const [newUserModal, setNewUserModal] = useState(false);
	const [editUserModal, setEditUserModal] = useState<users.user>();
	const [editPermission, setEditPermission] = useState<users.user>();
	const [editRoleModal, setEditRoleModal] = useState<users.user>();
	const [editEmailModal, setEditEmailModal] = useState<users.user>();
	const [data, setData] = useState<users.user[]>([]);
	const [roles, setRoles] = useState<users.roles[]>([]);

	const handleLoadRows = useCallback(async () => {
		setPending(true);
		clearTimeout(timeout);
		timeout = setTimeout(() => {
			resultRef.current?.scrollTo({
				top: 0,
				left: 0,
				behavior: "smooth",
			});
			request
				.get(`/admin/users?email=${search.trim()}`)
				.then((res) => res.data)
				.then((d) => {
					setData(d);
				})
				.catch((error) => {
					const errorMessage =
						error?.response?.data?.message ||
						error.message ||
						"An unknown error occurred.";
					toast.error(`Error fetching users: ${errorMessage}`);
				})
				.finally(() => setPending(false));
		}, 400);
	}, [search]);

	const handleLoadRoles = useCallback(async () => {
		request
			.get(`/admin/users/roles`)
			.then((res) => res.data)
			.then((d) => {
				setRoles(d);
			});
	}, []);

	React.useEffect(() => {
		handleLoadRows();
		handleLoadRoles();
	}, [handleLoadRows, handleLoadRoles]);

	return (
		<div className={styles.results} ref={resultRef}>
			<div className={styles.container}>
				<div className={styles.buttonGroup}>
					<CheckPermission permissions={["create_user"]}>
						<Button color="green" onClick={() => setNewUserModal(true)}>
							New user
						</Button>
					</CheckPermission>
				</div>
				<div className={styles.search}>
					<Input
						type="string"
						value={search}
						onInput={(v) => setSearch(v)}
						placeholder="Search"
					/>
				</div>
				{pending && (
					<div className={styles.tableLoading}>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
						<div className={styles.tableLoadingRow}>
							<ButtonLoader />
						</div>
					</div>
				)}
				{!pending && data?.length !== 0 && (
					<div className={styles.table}>
						{data.map((item) => (
							<div className={styles.tableRowWrap} key={item.id}>
								<div className={styles.tableRow}>
									<div className={styles.item}>
										<span className={styles.span}>Create date</span>
										{new Date(item.created_at).toLocaleString()}
									</div>
									<div className={styles.item}>
										<span className={styles.span}>Email</span>
										{item.email}
									</div>
									<div className={styles.item}>
										<span className={styles.span}>Role</span>
										{item.role}
									</div>
									<div
										className={classNames(
											styles.item,
											item.isActive ? styles.green : styles.red,
										)}
									>
										<span className={styles.span}>Status</span>
										{item.isActive ? "Active" : "Blocked"}
									</div>

									{item.id !== user.id && (
										<>
											<CheckPermission permissions={["manage_users"]}>
												<div className={styles.item}>
													{item.role !== "root" && (
														<Button onClick={() => setEditPermission(item)}>
															Permissions
														</Button>
													)}
												</div>
											</CheckPermission>
											<CheckPermission permissions={["manage_users"]}>
												<div className={styles.item}>
													{item.role !== "root" && item.role !== user.role && (
														<Button onClick={() => setEditEmailModal(item)}>
															Edit email
														</Button>
													)}
												</div>
											</CheckPermission>

											<CheckPermission permissions={["manage_users"]}>
												<div className={styles.item}>
													{item.role !== "root" && item.role !== user.role && (
														<Button onClick={() => setEditUserModal(item)}>
															Edit password
														</Button>
													)}
												</div>
											</CheckPermission>

											<CheckPermission permissions={["manage_users"]}>
												<div className={styles.item}>
													{(item.role !== "root" || user.role === "root") && (
														<Button onClick={() => setEditRoleModal(item)}>
															Edit role
														</Button>
													)}
												</div>
											</CheckPermission>
											{(user.role === "root" ||
												(user.role === "admin" && item.role !== "root")) && (
												<CheckPermission permissions={["manage_users"]}>
													<div className={styles.item}>
														{!(
															item.role === "root" && user.role !== "root"
														) && <BlockButton user={item} setData={setData} />}
													</div>
												</CheckPermission>
											)}
										</>
									)}
								</div>
							</div>
						))}
					</div>
				)}
				{!pending && data?.length === 0 && (
					<div className={styles.tableIsEmptyInfo}>No users</div>
				)}
			</div>
			<CSSTransition
				in={!!newUserModal}
				timeout={150}
				unmountOnExit
				classNames={{
					enter: modalStyles.modalEnter,
					enterActive: modalStyles.modalEnterActive,
					exit: modalStyles.modalExit,
					exitActive: modalStyles.modalExitActive,
				}}
			>
				<CreateModal
					roles={roles}
					onClose={() => setNewUserModal(false)}
					onCreate={() => {
						setNewUserModal(false);
						handleLoadRows();
					}}
				/>
			</CSSTransition>
			<CSSTransition
				in={!!editUserModal}
				timeout={150}
				unmountOnExit
				classNames={{
					enter: modalStyles.modalEnter,
					enterActive: modalStyles.modalEnterActive,
					exit: modalStyles.modalExit,
					exitActive: modalStyles.modalExitActive,
				}}
			>
				<EditUserModal
					selectedUser={editUserModal}
					onClose={() => setEditUserModal(undefined)}
					onEdit={() => {
						setEditUserModal(undefined);
						handleLoadRows();
					}}
				/>
			</CSSTransition>
			<CSSTransition
				in={!!editRoleModal}
				timeout={150}
				unmountOnExit
				classNames={{
					enter: modalStyles.modalEnter,
					enterActive: modalStyles.modalEnterActive,
					exit: modalStyles.modalExit,
					exitActive: modalStyles.modalExitActive,
				}}
			>
				<UpdateRoleModal
					selectedUser={editRoleModal}
					roles={roles}
					onClose={() => setEditRoleModal(undefined)}
					onEdit={() => {
						setEditRoleModal(undefined);
						handleLoadRows();
					}}
				/>
			</CSSTransition>
			<CSSTransition
				in={!!editPermission}
				timeout={150}
				unmountOnExit
				classNames={{
					enter: modalStyles.modalEnter,
					enterActive: modalStyles.modalEnterActive,
					exit: modalStyles.modalExit,
					exitActive: modalStyles.modalExitActive,
				}}
			>
				<EditUserPermissionsModal
					selectedUser={editPermission}
					onClose={() => setEditPermission(undefined)}
				/>
			</CSSTransition>
			<CSSTransition
				in={!!editEmailModal}
				timeout={150}
				unmountOnExit
				classNames={{
					enter: modalStyles.modalEnter,
					enterActive: modalStyles.modalEnterActive,
					exit: modalStyles.modalExit,
					exitActive: modalStyles.modalExitActive,
				}}
			>
				<UpdateEmailModal
					selectedUser={editEmailModal}
					onClose={() => setEditEmailModal(undefined)}
					onEdit={() => {
						setEditEmailModal(undefined);
						handleLoadRows();
					}}
				/>
			</CSSTransition>
		</div>
	);
};

const BlockButton = ({
	user,
	setData,
}: {
	user: global.user;
	setData: React.Dispatch<React.SetStateAction<users.user[]>>;
}) => {
	const [pending, setPending] = React.useState(false);

	const handleToggleUserBlock = (id: string) => {
		setPending(true);
		request
			.put(`/admin/users/${id}/block`)
			.then(() => {
				setData((prev) =>
					prev.map((user) => ({
						...user,
						isActive: user.id === id ? !user.isActive : user.isActive,
					})),
				);
			})
			.catch((error) => {
				const errorMessage =
					error.response?.data?.message || "Error blocking user";
				toast.error(errorMessage);
			})
			.finally(() => setPending(false));
	};

	return (
		<Button
			pending={pending}
			color={user.isActive ? "red" : "blue"}
			onClick={(e) => {
				e.stopPropagation();
				handleToggleUserBlock(user.id);
			}}
		>
			{user.isActive ? "Block" : "Unblock"}
		</Button>
	);
};

export default Users;
